import { OnboardPage } from './onboard-helper'

/**
 * Points earned per onboarding step completion
 * 
 * This constant aligns with backend PointsReason.ONBOARD_STEP_COMPLETED.
 * Each rewarded step completion grants 5 points.
 */
export const ONBOARD_STEP_POINTS = 5

/**
 * Points earned per onboarding step
 * 
 * This mapping aligns with backend PointsReason.ONBOARD_STEP_COMPLETED.
 * Each step completion grants 5 points.
 * 
 * Note: CHOOSE_ROLE, SKIP, and DONE steps do not award points.
 */
export const ONBOARDING_STEP_POINTS: Partial<Record<OnboardPage, number>> = {
  PERSONALIZE: ONBOARD_STEP_POINTS,
  INGREDIENTS: ONBOARD_STEP_POINTS,
  CUISINES: ONBOARD_STEP_POINTS,
  PREPARATIONS: ONBOARD_STEP_POINTS,
  AMBIENCE: ONBOARD_STEP_POINTS,
  DIETARY_PREFERENCES: ONBOARD_STEP_POINTS,
  ALLERGIES: ONBOARD_STEP_POINTS,
  FAVORITES: ONBOARD_STEP_POINTS,
  DINING_FREQUENCY: ONBOARD_STEP_POINTS,
  SOCIAL_MEDIA: ONBOARD_STEP_POINTS,
}

/**
 * Get points for a specific onboarding step
 * 
 * @param page - The OnboardPage enum value or page string
 * @returns Points earned for the step (5), or null if step doesn't award points
 */
export function getStepPoints(page: OnboardPage | string | null | undefined): number | null {
  if (!page) return null
  
  // Handle string page values
  if (typeof page === 'string') {
    // Exclude non-rewarded steps
    if (page === 'CHOOSE_ROLE' || page === 'SKIP' || page === 'DONE') {
      return null
    }
    
    // Try to convert string to OnboardPage enum
    const pageEnum = OnboardPage[page as keyof typeof OnboardPage] as OnboardPage
    if (pageEnum && ONBOARDING_STEP_POINTS[pageEnum] !== undefined) {
      return ONBOARDING_STEP_POINTS[pageEnum] ?? null
    }
    return null
  }
  
  // Handle OnboardPage enum
  return ONBOARDING_STEP_POINTS[page] ?? null
}

/**
 * Get cumulative points earned so far during onboarding
 * 
 * @param completedStepCount - Number of rewarded steps completed
 * @returns Total points earned (completedStepCount * ONBOARD_STEP_POINTS)
 */
export function getOnboardingPointsSoFar(completedStepCount: number): number {
  return completedStepCount * ONBOARD_STEP_POINTS
}

/**
 * Get points for a specific onboarding step (backward compatibility)
 * 
 * @param page - The OnboardPage enum value
 * @returns Points earned for the step, or undefined if step doesn't award points
 * @deprecated Use getStepPoints instead
 */
export function getOnboardingStepPoints(page: OnboardPage): number | undefined {
  const points = getStepPoints(page)
  return points !== null ? points : undefined
}
